import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useLegalDocuments } from "@/hooks/useLegalDocuments";
import type { LegalDocument, LegalDocumentStatus } from "@/interfaces/legal";
import UploadTermsDialog from "./UploadTermsDialog";

const PER_PAGE = 10;

type SortColumn = "effectiveAt" | "uploadedAt";

const STATUS_STYLE: Record<LegalDocumentStatus, { label: string; bg: string; fg: string }> = {
  current: { label: "Current", bg: "var(--color-good-sage)", fg: "#fff" },
  scheduled: { label: "Scheduled", bg: "var(--color-good-ochre-4)", fg: "var(--color-good-ochre)" },
  superseded: { label: "Superseded", bg: "var(--color-wax)", fg: "var(--color-graphite)" },
};

const fmtDate = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T00:00`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const fmtInstant = (iso: string) =>
  `${fmtDate(iso)}, ${new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;

/**
 * System › Terms of Service — the published versions, and the button that adds one.
 *
 * READ AND APPEND ONLY. There is no edit and no delete anywhere on this screen, because there is
 * none in the API either: a published version's object is permanent and acceptance rows point at
 * it, so a correction is a new upload that supersedes rather than replaces.
 *
 * Each row carries its storage key and hash, which is what makes the table evidence rather than a
 * list: given a row, someone can fetch the exact object a client accepted and verify it is
 * unchanged. The key is truncated to its filename with the full value on hover — at full width it
 * pushed every other column off the screen.
 *
 * Sorting and paging are client-side. The API returns the whole history deliberately (it is the
 * audit surface), and a platform document rotates a few times a year, so this list is tens of rows
 * at most — a server round trip per page would cost more than it saves.
 */
export default function TermsOfServicePanel() {
  const { data: documents = [], isLoading, error } = useLegalDocuments();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortColumn>("uploadedAt");
  const [direction, setDirection] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => {
    const rows = [...documents].sort((a, b) => a[sortBy].localeCompare(b[sortBy]));
    return direction === "asc" ? rows : rows.reverse();
  }, [documents, sortBy, direction]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  const shown = sorted.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  function toggleSort(column: SortColumn) {
    if (column === sortBy) setDirection(direction === "asc" ? "desc" : "asc");
    else {
      setSortBy(column);
      setDirection("desc");
    }
    setPage(1); // a re-sort with the reader left on page 3 shows them an arbitrary slice
  }

  return (
    <Box sx={{ maxWidth: 1000 }}>
      <Box
        sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}
      >
        <Box sx={{ pr: 2 }}>
          <Typography sx={{ fontFamily: "var(--font-crimson)", fontSize: 20 }}>
            Terms of Service
          </Typography>
          <Typography
            sx={{ fontSize: 13.5, color: "var(--color-graphite)", mt: 0.5, maxWidth: 560 }}
          >
            The Platform Terms every new organization accepts at signup. Uploading a version
            publishes it — the file is stored once and never changed. Nothing here is edited or
            deleted; a correction is a new upload.
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => setDialogOpen(true)}
          sx={{ bgcolor: "var(--color-rodeo-black)", flexShrink: 0 }}
        >
          Upload new version
        </Button>
      </Box>

      {isLoading && <CircularProgress size={22} />}
      {error && (
        <Typography sx={{ fontSize: 13.5, color: "var(--color-good-wine)" }}>
          Couldn't load the published versions.
        </Typography>
      )}

      {!isLoading && !error && documents.length === 0 && (
        <Box
          sx={{
            border: "1px solid var(--color-card-border)",
            borderRadius: "12px",
            p: 3,
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: 13.5, color: "var(--color-graphite)" }}>
            No version has been published yet. Until one is, signup cannot ask anyone to accept the
            Platform Terms.
          </Typography>
        </Box>
      )}

      {documents.length > 0 && (
        <>
          <Table
            size="small"
            sx={{ border: "1px solid var(--color-card-border)", borderRadius: 2 }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "var(--color-clear-dust)" }}>
                <TableCell>Version</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sortDirection={sortBy === "effectiveAt" ? direction : false}>
                  <TableSortLabel
                    active={sortBy === "effectiveAt"}
                    direction={sortBy === "effectiveAt" ? direction : "desc"}
                    onClick={() => toggleSort("effectiveAt")}
                  >
                    Effective
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === "uploadedAt" ? direction : false}>
                  <TableSortLabel
                    active={sortBy === "uploadedAt"}
                    direction={sortBy === "uploadedAt" ? direction : "desc"}
                    onClick={() => toggleSort("uploadedAt")}
                  >
                    Uploaded
                  </TableSortLabel>
                </TableCell>
                <TableCell>By</TableCell>
                <TableCell>Orgs</TableCell>
                <TableCell>File</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((doc: LegalDocument) => (
                <TableRow
                  key={doc.id}
                  sx={{ bgcolor: doc.status === "current" ? "var(--color-clear-dust)" : undefined }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{doc.version}</Typography>
                      {doc.isMaterial && (
                        <Chip
                          size="small"
                          label="Material"
                          sx={{
                            height: 18,
                            fontSize: 10,
                            fontWeight: 700,
                            bgcolor: "var(--color-good-night-bg)",
                            color: "var(--color-good-night)",
                          }}
                        />
                      )}
                    </Box>
                    {/* The full key on hover: it is the evidence trail, but at full width it pushed
                        every other column off the screen. */}
                    <Typography
                      title={`${doc.storageKey} · sha256 ${doc.contentHash}`}
                      sx={{ fontFamily: "monospace", fontSize: 10.5, color: "#8b8b8b", mt: 0.3 }}
                    >
                      {doc.storageKey.split("/").pop()} · sha256 {doc.contentHash.slice(0, 8)}…
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={STATUS_STYLE[doc.status].label}
                      sx={{
                        height: 19,
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        bgcolor: STATUS_STYLE[doc.status].bg,
                        color: STATUS_STYLE[doc.status].fg,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                    {fmtDate(doc.effectiveAt)}
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                    {fmtInstant(doc.uploadedAt)}
                  </TableCell>
                  <TableCell sx={{ color: "var(--color-graphite)", whiteSpace: "nowrap" }}>
                    {doc.uploadedByName ?? "—"}
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {doc.acceptanceCount}
                  </TableCell>
                  <TableCell>
                    <Link
                      href={doc.url}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ fontSize: 12.5, fontWeight: 600, color: "var(--color-dust-ink)" }}
                    >
                      View PDF
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.5 }}
          >
            <Typography sx={{ fontSize: 12.5, color: "var(--color-graphite)" }}>
              {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, sorted.length)} of{" "}
              {sorted.length} versions
            </Typography>
            {pageCount > 1 && (
              <Pagination
                page={page}
                count={pageCount}
                size="small"
                onChange={(_, next) => setPage(next)}
              />
            )}
          </Box>
        </>
      )}

      <UploadTermsDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </Box>
  );
}
